import { TextInput, Button, Checkbox, Input } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateEntities } from '../../queries/use-create-entity';
import { toastNotification } from '../../components/toast/Toast';
import { EntityTypeEnum } from '../../types/entity';
import DaySelect from '../../components/day-select/DaySelect';
import MonthSelect from '../../components/month-select/MonthSelect';
import EntityTypeSelect from '../../components/entity-type-select/EntityTypeSelect';
import GroupSelect from '../../components/group-select/GroupSelect';

type ManualSingleEntityFormProps = {
    onLoading: () => void;
    onSubmit: () => void;
};

function ManualSingleEntityForm({ onLoading, onSubmit }: ManualSingleEntityFormProps): JSX.Element {
    const { shortId } = useFirmContext();
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState<string | undefined>();
    const [legalName, setLegalName] = useState<{ value?: string; errorMessage?: string }>({ value: '' });
    const [useSameName, setUseSameName] = useState<boolean>(false);

    const [params] = useSearchParams();
    const defaultGroup = params.get('groupId');
    const returnTo = params.get('returnTo');
    const [selectedEntityType, setSelectedEntityType] = useState<EntityTypeEnum | undefined>();
    const [entityTypeError, setEntityTypeError] = useState<string | undefined>();
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [industryError, setIndustryError] = useState<string | undefined>();
    const [selectedGroups, setSelectedGroups] = useState<string[]>(defaultGroup ? [defaultGroup] : []);
    const [selectedMonth, setSelectedMonth] = useState<number>();
    const [financialEndDateError, setFinancialEndDateError] = useState<string | undefined>();
    const [selectedDay, setSelectedDay] = useState<number>();

    const createEntitiesQuery = useCreateEntities();
    const navigate = useNavigate();

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        let hasErrors = false;
        if (!name) {
            setNameError('A unique name is required.');
            hasErrors = true;
        }

        if (!legalName.value) {
            setLegalName((prev) => ({ ...prev, errorMessage: 'A unique legal name is required.' }));
            hasErrors = true;
        }

        if (!selectedMonth || !selectedDay) {
            setFinancialEndDateError(' A valid financial end date is required.');
            hasErrors = true;
        }

        if (!selectedEntityType) {
            setEntityTypeError('Please select an entity type.');
            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        onLoading();

        createEntitiesQuery.mutate(
            {
                entities: [
                    {
                        name,
                        legalName: legalName.value as string,
                        type: selectedEntityType as EntityTypeEnum,
                        industry: selectedIndustry,
                        groups: selectedGroups,
                        financialYearEndDay: selectedDay as number,
                        financialYearEndMonth: selectedMonth as number,
                    },
                ],
            },
            {
                onSuccess: ({ results }) => {
                    navigate(`/${shortId}/entities/${results[0].data?.id}`);
                    toastNotification('success', 'New entity created!');
                },
                onError: (err) => {
                    const { name, legalName, type, industry, financialYearEndDay, financialYearEndMonth } =
                        err.response?.data.results[0].error.fields;

                    setNameError(name);
                    setLegalName((prev) => ({ ...prev, errorMessage: legalName }));
                    setEntityTypeError(type);
                    setIndustryError(industry);

                    if (financialYearEndDay || financialYearEndMonth) {
                        setFinancialEndDateError('Please enter a valid financial end date.');
                    }

                    onSubmit();
                    toastNotification('error', 'Unable to create new entity!');
                },
            }
        );
    }

    function handleMonthChange(i: number): void {
        setSelectedMonth(i);
        setSelectedDay(1);
        setFinancialEndDateError(undefined);
    }

    useEffect(() => {
        if (name && useSameName) {
            setLegalName({ value: name, errorMessage: undefined });
        } else if (!name) {
            setLegalName({ value: '', errorMessage: undefined });
        } else {
            setLegalName((prev) => ({ ...prev }));
        }
    }, [useSameName, name]);

    return (
        <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
            {/* ENTITY NAME */}
            <div className="flex flex-col gap-2">
                <TextInput
                    placeholder="e.g. Joe & Marcia Bloggs"
                    title="Entity name"
                    defaultValue={name}
                    onChange={(val): void => {
                        setName(val);
                        setNameError(undefined);
                    }}
                    errorMessage={nameError}
                    isDisabled={createEntitiesQuery.isLoading}
                    autoFocus
                />
                <Checkbox label="Use as legal name" onChange={(): void => setUseSameName(!useSameName)} />
            </div>

            {/* ENTITY LEGAL NAME */}
            <Input
                label="Legal Name"
                type="text"
                placeholder="Legal name for the entity..."
                onChange={(e): void => {
                    setLegalName({ value: e.target.value, errorMessage: undefined });
                }}
                errorMessage={legalName?.errorMessage}
                value={legalName.value}
                disabled={useSameName}
            />

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financial year end date</label>
                <div className="flex items-center justify-between [&>*]:flex-1 gap-4">
                    {/* FINANCIAL YEAR END MONTH SELECT */}
                    <MonthSelect
                        onChange={(i): void => {
                            handleMonthChange(i[0]);
                            setSelectedDay(undefined);
                        }}
                        isDisabled={createEntitiesQuery.isLoading}
                        defaultValue={selectedMonth}
                    />

                    {/* FINANCIAL YEAR END DAY SELECT */}
                    <DaySelect
                        selectedMonth={selectedMonth}
                        defaultValue={selectedDay}
                        isDisabled={!selectedMonth || createEntitiesQuery.isLoading}
                        onChange={(i): void => {
                            setSelectedDay(i[0]);
                            setFinancialEndDateError(undefined);
                        }}
                    />
                </div>
                {financialEndDateError && <p className="mt-2 text-sm text-red-600">{financialEndDateError}</p>}
            </div>

            {/* ENTITY TYPE SELECT */}
            <EntityTypeSelect
                title="Entity type"
                placeholder="Select an entity type..."
                onChange={(options): void => {
                    setSelectedEntityType(options[0]);
                    setEntityTypeError(undefined);
                }}
                defaultEntityType={selectedEntityType ? [selectedEntityType] : []}
                errorMessage={entityTypeError}
                isDisabled={createEntitiesQuery.isLoading}
            />

            {/* GROUP SELECT */}
            <GroupSelect
                onChange={setSelectedGroups}
                title="Group"
                isOptional
                isDisabled={createEntitiesQuery.isLoading}
                defaultValue={params.get('groupId') || undefined}
            />

            {/* FORM CANCEL + SUBMIT */}
            <div className="flex justify-end gap-4">
                <Button type="hollow" label="Cancel" onClick={(): void => navigate(returnTo || '..')} />
                <Button
                    type="primary"
                    label={createEntitiesQuery.isLoading ? 'Creating...' : 'Create Entity'}
                    disabled={createEntitiesQuery.isLoading}
                    isLoading={createEntitiesQuery.isLoading}
                    isSubmit
                />
            </div>
        </form>
    );
}
export default ManualSingleEntityForm;
